import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Tag,
  Button,
  InlineLoading,
  Accordion,
  AccordionItem,
  Modal,
} from '@carbon/react';
import {
  Close,
  DataBase,
  Document,
  FlowData,
  Launch,
  TextShortParagraph,
  ChevronRight,
} from '@carbon/icons-react';
import { useQuery } from '@tanstack/react-query';
import { explorationApi } from '@/lib/api/exploration.api';
import { knowledgeUnitApi } from '@/lib/api/knowledge-unit.api';
import KnowledgeUnitCard from '@/components/knowledge-base/KnowledgeUnitCard';
import type { ConceptGrounding, ConceptKOEvidence } from '@snxt/snxt-onto-share';
import './EvidencePanel.scss';

interface EvidencePanelProps {
  explorationId: string;
  nodeId: string | null;
  nodeLabel: string | null;
  nodeLayer: string | null;
  onClose: () => void;
}

/**
 * Slide-in panel that shows traceability evidence for a selected node.
 *
 * - Layer 'ontology' (default): shows concept groundings (KO → KU accordion)
 * - Layer 'instance': shows extracted attributes + description + contributing KUs
 * - Layer 'ku': shows compact KU detail (L1 / L2 / L3) + "Open full details" modal
 * - Layer 'ko' / 'kb': shows a simple metadata summary (no API call)
 */
export function EvidencePanel({ explorationId, nodeId, nodeLabel, nodeLayer, onClose }: EvidencePanelProps) {
  const navigate = useNavigate();
  const isConceptNode = !nodeLayer || nodeLayer === 'ontology';
  const isInstanceNode = nodeLayer === 'instance';
  const isKuNode = nodeLayer === 'ku';
  const isSourceNode = nodeLayer === 'ko' || nodeLayer === 'kb';

  // KU full-detail modal state
  const [kuModalOpen, setKuModalOpen] = useState(false);

  // Close modal when the selected node changes
  useEffect(() => {
    setKuModalOpen(false);
  }, [nodeId]);

  // Fetch concept evidence (only for ontology-layer nodes)
  const evidenceQuery = useQuery({
    queryKey: ['exploration-evidence', explorationId, nodeId],
    queryFn: () => explorationApi.getConceptEvidence(explorationId, nodeId!),
    enabled: !!nodeId && isConceptNode,
    staleTime: 60_000,
  });

  // Strip the "ku-" prefix to get the raw DB uuid (node IDs in the graph
  // are "ku-<uuid>" to namespace them within React Flow, but the API expects
  // just the bare uuid).
  const kuDbId = nodeId?.replace(/^ku-/, '') ?? null;

  // Fetch full KU detail (only for KU-layer nodes)
  const kuDetailQuery = useQuery({
    queryKey: ['ku-detail', kuDbId],
    queryFn: () => knowledgeUnitApi.getDetail(kuDbId!),
    enabled: !!kuDbId && isKuNode,
    staleTime: 60_000,
  });

  // Close panel when Escape is pressed
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (kuModalOpen) {
          setKuModalOpen(false);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, kuModalOpen]);

  // Compute average similarity from groundings when data is available
  const evidenceData = evidenceQuery.data;
  const avgSim =
    evidenceData && evidenceData.total_groundings > 0
      ? (() => {
          const allGroundings: ConceptGrounding[] = evidenceData.by_ko.flatMap((ko) => ko.groundings);
          if (allGroundings.length === 0) return null;
          const sum = allGroundings.reduce((acc, g) => acc + g.similarity_score, 0);
          return sum / allGroundings.length;
        })()
      : null;

  const kuDetail = kuDetailQuery.data;

  return (
    <>
      <div
        data-testid="explorer-evidence-panel"
        className={`evidence-panel ${nodeId ? 'evidence-panel--open' : ''}`}
      >
        {/* Header */}
        <div className="evidence-panel__header">
          <div className="evidence-panel__title">
            {isInstanceNode ? (
              <FlowData size={16} />
            ) : isKuNode ? (
              <TextShortParagraph size={16} />
            ) : (
              <DataBase size={16} />
            )}
            <span>
              {isInstanceNode ? 'Instance' : isKuNode ? 'Knowledge Unit' : 'Evidence'}
            </span>
            {nodeLabel && (
              <Tag
                size="sm"
                type={isInstanceNode ? 'teal' : isKuNode ? 'green' : 'blue'}
                className="evidence-panel__concept-tag"
              >
                {nodeLabel}
              </Tag>
            )}
          </div>
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={Close}
            iconDescription="Close evidence panel"
            onClick={onClose}
          />
        </div>

        {/* Body */}
        <div className="evidence-panel__body">
          {!nodeId && (
            <p className="evidence-panel__empty">Click a node to see details.</p>
          )}

          {/* ── KU nodes — compact detail flyout ── */}
          {nodeId && isKuNode && (
            <KuFlyout
              kuDetail={kuDetail ?? null}
              isLoading={kuDetailQuery.isLoading}
              isError={kuDetailQuery.isError}
              onOpenModal={() => setKuModalOpen(true)}
              onNavigateToKo={(kbId, koId) =>
                navigate(`/knowledge-bases/${kbId}/objects/${koId}`)
              }
            />
          )}

          {/* ── Source nodes (ko/kb) — simple metadata ── */}
          {nodeId && isSourceNode && (
            <div className="evidence-panel__source-meta">
              <Tag size="sm" type="outline">
                {nodeLayer?.toUpperCase()}
              </Tag>
              <p className="evidence-panel__source-label">{nodeLabel}</p>
              <p className="evidence-panel__empty" style={{ marginTop: '0.5rem' }}>
                {nodeLayer === 'ko'
                  ? 'This is a Knowledge Object node.'
                  : 'This is a Knowledge Base node.'}
              </p>
            </div>
          )}

          {/* ── Instance nodes ── */}
          {nodeId && isInstanceNode && (
            <InstanceDetail explorationId={explorationId} nodeId={nodeId} />
          )}

          {/* ── Concept nodes (ontology layer) ── */}
          {nodeId && isConceptNode && evidenceQuery.isLoading && (
            <InlineLoading description="Loading evidence…" />
          )}

          {nodeId && isConceptNode && evidenceQuery.isError && (
            <p className="evidence-panel__error">Failed to load evidence.</p>
          )}

          {nodeId && isConceptNode && evidenceData && evidenceData.by_ko.length === 0 && (
            <p className="evidence-panel__empty">
              No grounding found for this concept yet. Run the populate pipeline first.
            </p>
          )}

          {nodeId && isConceptNode && evidenceData && evidenceData.by_ko.length > 0 && (
            <>
              {/* Summary row */}
              <div className="evidence-panel__summary">
                <Tag size="sm" type="blue">
                  {evidenceData.total_groundings} KU{evidenceData.total_groundings !== 1 ? 's' : ''}
                </Tag>
                <Tag size="sm" type="teal">
                  {evidenceData.by_ko.length} KO{evidenceData.by_ko.length !== 1 ? 's' : ''}
                </Tag>
                {avgSim != null && (
                  <Tag size="sm" type="green">
                    avg {Math.round(avgSim * 100)}% similarity
                  </Tag>
                )}
              </div>

              {/* Per-KO accordion — semantic isolation boundary */}
              <Accordion className="evidence-panel__accordion">
                {evidenceData.by_ko.map((ko) => (
                  <AccordionItem
                    key={ko.knowledge_object_id}
                    title={
                      <div className="evidence-panel__ko-title">
                        <Document size={14} />
                        <span className="evidence-panel__ko-name">
                          {ko.knowledge_object_name ?? ko.knowledge_object_id}
                        </span>
                        <Tag size="sm" type="gray">
                          {ko.groundings.length} KU
                        </Tag>
                      </div>
                    }
                  >
                    <div className="evidence-panel__units">
                      <p className="evidence-panel__kb-name">{ko.knowledge_base_name}</p>
                      {ko.groundings.map((g) => (
                        <div key={g.id} className="evidence-panel__unit">
                          <div className="evidence-panel__unit-meta">
                            <Tag
                              size="sm"
                              type={
                                g.similarity_score >= 0.75
                                  ? 'green'
                                  : g.similarity_score >= 0.5
                                    ? 'warm-gray'
                                    : 'gray'
                              }
                            >
                              {Math.round(g.similarity_score * 100)}%
                            </Tag>
                            <Tag size="sm" type="outline">
                              {g.matched_layer === 'l2_summary' ? 'L2 summary' : 'L1 unit'}
                            </Tag>
                          </div>
                          {g.knowledge_unit_content && (
                            <p className="evidence-panel__unit-text">{g.knowledge_unit_content}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </AccordionItem>
                ))}
              </Accordion>
            </>
          )}
        </div>
      </div>

      {/* ── KU full-detail modal ──────────────────────────────────────────────── */}
      {kuModalOpen && kuDetail && (
        <Modal
          open
          passiveModal
          size="lg"
          modalHeading={
            kuDetail.unit.label
              ? `KU #${kuDetail.unit.ordinal} — ${kuDetail.unit.label}`
              : `Knowledge Unit #${kuDetail.unit.ordinal}`
          }
          onRequestClose={() => setKuModalOpen(false)}
          className="evidence-panel__ku-modal"
        >
          <div className="evidence-panel__ku-modal-body">
            {/* Modal breadcrumb */}
            <div className="evidence-panel__ku-modal-breadcrumb">
              <span>{kuDetail.context.knowledge_base.name}</span>
              <ChevronRight size={14} />
              <button
                className="evidence-panel__ku-modal-ko-link"
                onClick={() => {
                  setKuModalOpen(false);
                  navigate(
                    `/knowledge-bases/${kuDetail.context.knowledge_object.knowledge_base_id}/objects/${kuDetail.context.knowledge_object.id}`
                  );
                }}
              >
                {kuDetail.context.knowledge_object.filename}
              </button>
              <span className="evidence-panel__ku-modal-position">
                #{kuDetail.unit.ordinal} of {kuDetail.context.total_units}
              </span>
            </div>

            <KnowledgeUnitCard
              unit={kuDetail.unit}
              summary={kuDetail.summary ?? undefined}
              extractions={kuDetail.extractions}
              forceExpanded
              knowledgeObjectId={kuDetail.context.knowledge_object.id}
              layout="rows"
            />
          </div>
        </Modal>
      )}
    </>
  );
}

// ── KU Flyout sub-component ────────────────────────────────────────────────────

interface KuFlyoutProps {
  kuDetail: import('@/lib/api/knowledge-unit.api').KnowledgeUnitDetail | null;
  isLoading: boolean;
  isError: boolean;
  onOpenModal: () => void;
  onNavigateToKo: (kbId: string, koId: string) => void;
}

function KuFlyout({ kuDetail, isLoading, isError, onOpenModal, onNavigateToKo }: KuFlyoutProps) {
  if (isLoading) {
    return <InlineLoading description="Loading knowledge unit…" />;
  }

  if (isError || !kuDetail) {
    return <p className="evidence-panel__error">Failed to load knowledge unit details.</p>;
  }

  const { unit, summary, extractions, context } = kuDetail;
  const hasL2 = !!summary;
  const hasL3 = extractions.length > 0;

  // Deduplicate extraction kinds
  const extractionKinds = [...new Set(extractions.map((e) => e.extraction_kind))];

  return (
    <div className="evidence-panel__ku-detail">
      {/* Breadcrumb */}
      <div className="evidence-panel__ku-breadcrumb">
        <DataBase size={12} />
        <span>{context.knowledge_base.name}</span>
        <ChevronRight size={12} />
        <Document size={12} />
        <button
          className="evidence-panel__ku-breadcrumb-link"
          onClick={() =>
            onNavigateToKo(
              context.knowledge_object.knowledge_base_id,
              context.knowledge_object.id
            )
          }
          title="Open Knowledge Object"
        >
          {context.knowledge_object.filename}
        </button>
      </div>

      {/* Meta row */}
      <div className="evidence-panel__ku-meta">
        <Tag size="sm" type="outline">#{unit.ordinal}</Tag>
        <Tag size="sm" type="blue">{unit.unit_type}</Tag>
        {unit.content_char_count != null && (
          <span className="evidence-panel__ku-char-count">
            {unit.content_char_count.toLocaleString()} chars
          </span>
        )}
        <span className="evidence-panel__ku-position">
          {unit.ordinal} of {context.total_units}
        </span>
      </div>

      {/* L1 — Raw content */}
      <div className="evidence-panel__ku-section">
        <div className="evidence-panel__ku-section-header">
          <span className="evidence-panel__ku-layer-badge">L1</span>
          <span className="evidence-panel__ku-section-title">Raw content</span>
        </div>
        <div className="evidence-panel__ku-content">
          {unit.content ? (
            <pre className="evidence-panel__ku-pre">{unit.content}</pre>
          ) : (
            <p className="evidence-panel__empty">No raw content available.</p>
          )}
        </div>
      </div>

      {/* L2 — AI Summary */}
      <div className="evidence-panel__ku-section evidence-panel__ku-section--l2">
        <div className="evidence-panel__ku-section-header">
          <span className="evidence-panel__ku-layer-badge evidence-panel__ku-layer-badge--l2">L2</span>
          <span className="evidence-panel__ku-section-title">
            {unit.unit_type === 'image' ? 'AI Caption' : 'AI Summary'}
          </span>
          {hasL2 && summary!.model_id && (
            <span className="evidence-panel__ku-model">{summary!.model_id}</span>
          )}
        </div>
        <div className="evidence-panel__ku-content">
          {hasL2 ? (
            <p className="evidence-panel__ku-summary">{summary!.summary}</p>
          ) : (
            <p className="evidence-panel__empty evidence-panel__empty--inline">
              {(unit.content_char_count ?? 0) <= 3000
                ? 'Below threshold — L1 fits embedding window directly'
                : 'Not yet summarised'}
            </p>
          )}
        </div>
      </div>

      {/* L3 — Extractions (kind tags only) */}
      <div className="evidence-panel__ku-section evidence-panel__ku-section--l3">
        <div className="evidence-panel__ku-section-header">
          <span className="evidence-panel__ku-layer-badge evidence-panel__ku-layer-badge--l3">L3</span>
          <span className="evidence-panel__ku-section-title">Extractions</span>
          {hasL3 && (
            <span className="evidence-panel__ku-model">
              {extractions.length} item{extractions.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="evidence-panel__ku-content">
          {hasL3 ? (
            <div className="evidence-panel__ku-extraction-kinds">
              {extractionKinds.map((kind) => (
                <Tag key={kind} size="sm" type="warm-gray">
                  {kind}
                </Tag>
              ))}
            </div>
          ) : (
            <p className="evidence-panel__empty evidence-panel__empty--inline">
              No extractions yet
            </p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="evidence-panel__ku-actions">
        <Button
          kind="primary"
          size="sm"
          renderIcon={Launch}
          onClick={onOpenModal}
        >
          Open full details
        </Button>
        <Button
          kind="ghost"
          size="sm"
          renderIcon={Launch}
          onClick={() =>
            onNavigateToKo(
              context.knowledge_object.knowledge_base_id,
              context.knowledge_object.id
            )
          }
        >
          Open in KB
        </Button>
      </div>
    </div>
  );
}

// ── Shared KO evidence accordion ──────────────────────────────────────────────

/**
 * Renders a per-KO accordion of KU groundings.
 * Used by both concept-evidence and instance-evidence panels.
 */
function KoEvidenceAccordion({ byKo }: { byKo: ConceptKOEvidence[] }) {
  return (
    <Accordion className="evidence-panel__accordion">
      {byKo.map((ko) => (
        <AccordionItem
          key={ko.knowledge_object_id}
          title={
            <div className="evidence-panel__ko-title">
              <Document size={14} />
              <span className="evidence-panel__ko-name">
                {ko.knowledge_object_name ?? ko.knowledge_object_id}
              </span>
              <Tag size="sm" type="gray">
                {ko.groundings.length} KU
              </Tag>
            </div>
          }
        >
          <div className="evidence-panel__units">
            <p className="evidence-panel__kb-name">{ko.knowledge_base_name}</p>
            {ko.groundings.map((g) => (
              <div key={g.id} className="evidence-panel__unit">
                <div className="evidence-panel__unit-meta">
                  <Tag
                    size="sm"
                    type={
                      g.similarity_score >= 0.75
                        ? 'green'
                        : g.similarity_score >= 0.5
                          ? 'warm-gray'
                          : 'gray'
                    }
                  >
                    {Math.round(g.similarity_score * 100)}%
                  </Tag>
                  <Tag size="sm" type="outline">
                    {g.matched_layer === 'l2_summary' ? 'L2 summary' : 'L1 unit'}
                  </Tag>
                </div>
                {g.knowledge_unit_content && (
                  <p className="evidence-panel__unit-text">{g.knowledge_unit_content}</p>
                )}
              </div>
            ))}
          </div>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

// ── Instance detail sub-component ─────────────────────────────────────────────

interface InstanceDetailProps {
  explorationId: string;
  /** React Flow node id, format: inst-<uuid> */
  nodeId: string;
}

/**
 * Fetches and renders the full detail for an extracted concept instance:
 * - Concept type tag
 * - Description
 * - Extraction confidence + model
 * - Structured attributes (key/value)
 * - Contributing KU evidence grouped by KO
 */
function InstanceDetail({ explorationId, nodeId }: InstanceDetailProps) {
  // Strip the "inst-" prefix to get the raw DB uuid
  const instanceId = nodeId.replace(/^inst-/, '');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['instance-evidence', explorationId, instanceId],
    queryFn: () => explorationApi.getInstanceEvidence(explorationId, instanceId),
    enabled: !!instanceId,
    staleTime: 60_000,
  });

  if (isLoading) {
    return <InlineLoading description="Loading instance details…" />;
  }

  if (isError || !data) {
    return <p className="evidence-panel__error">Failed to load instance details.</p>;
  }

  const { instance, total_groundings, by_ko } = data;
  const attrs = Object.entries(instance.attributes ?? {});

  const confidenceColor =
    instance.extraction_confidence == null
      ? 'outline'
      : instance.extraction_confidence >= 0.75
        ? 'green'
        : instance.extraction_confidence >= 0.5
          ? 'warm-gray'
          : 'gray';

  return (
    <div className="evidence-panel__instance">
      {/* Concept type */}
      <div className="evidence-panel__instance-section">
        <span className="evidence-panel__instance-section-label">Concept type</span>
        <Tag size="sm" type="blue">{instance.ontology_node_label}</Tag>
      </div>

      {/* Description */}
      {instance.description && (
        <div className="evidence-panel__instance-section">
          <span className="evidence-panel__instance-section-label">Description</span>
          <p className="evidence-panel__instance-description">{instance.description}</p>
        </div>
      )}

      {/* Confidence + model */}
      <div className="evidence-panel__instance-section evidence-panel__instance-meta-row">
        {instance.extraction_confidence != null && (
          <Tag size="sm" type={confidenceColor}>
            {Math.round(instance.extraction_confidence * 100)}% confidence
          </Tag>
        )}
        {instance.extraction_model && (
          <span className="evidence-panel__instance-model">{instance.extraction_model}</span>
        )}
      </div>

      {/* Attributes */}
      {attrs.length > 0 && (
        <div className="evidence-panel__instance-section">
          <span className="evidence-panel__instance-section-label">
            Attributes ({attrs.length})
          </span>
          <dl className="evidence-panel__instance-attrs">
            {attrs.map(([key, value]) => (
              <div key={key} className="evidence-panel__instance-attr-row">
                <dt className="evidence-panel__instance-attr-key">{key}</dt>
                <dd className="evidence-panel__instance-attr-value">
                  {typeof value === 'object'
                    ? JSON.stringify(value)
                    : String(value ?? '')}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {/* ID */}
      <div className="evidence-panel__instance-id">
        <Tag size="sm" type="outline">ID</Tag>
        <span className="evidence-panel__instance-id-value">{instanceId}</span>
      </div>

      {/* Evidence section */}
      <div className="evidence-panel__instance-section">
        <span className="evidence-panel__instance-section-label">KB evidence</span>
        {total_groundings === 0 ? (
          <p className="evidence-panel__empty">No contributing KUs found.</p>
        ) : (
          <>
            <div className="evidence-panel__summary">
              <Tag size="sm" type="blue">
                {total_groundings} KU{total_groundings !== 1 ? 's' : ''}
              </Tag>
              <Tag size="sm" type="teal">
                {by_ko.length} KO{by_ko.length !== 1 ? 's' : ''}
              </Tag>
            </div>
            <KoEvidenceAccordion byKo={by_ko} />
          </>
        )}
      </div>
    </div>
  );
}

// Made with Bob
